import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The wait and the reveal (well/product/23, boards K37–K39): three full-bleed
 * scenes, each a painted background with the parts that must be exact — the
 * tray, the print, your words, the line, the glow — built in HTML and CSS on
 * top. Only transform and opacity ever animate, so they hold 60fps on a phone;
 * the art is the variant, the components stay the same.
 *
 * - Darkroom: the Moon rocks the tray; your words are the one sharp thing on a
 *   print that is still only colour. Never a preview: the picture is not here.
 * - PrintOnPeg: the finished print hangs face-down and glowing. It is the button.
 * - PrintReveal: one tap turns it over, then the picture fills the screen, your
 *   words at its foot and a small Kraben who noticed.
 */

export function Stage({ bg, dim, className, children, label, onClick }: { bg: string; dim?: boolean; className?: string; children: ReactNode; label?: string; onClick?: () => void }) {
  return (
    <div className={`stage ${className ?? ''}`} role="region" aria-label={label} onClick={onClick}>
      <img className="stage-bg" src={bg} alt="" draggable={false} />
      {dim && <div className="stage-dim" />}
      {children}
    </div>
  );
}

function Hud({ day, right }: { day: number; right?: ReactNode }) {
  return (
    <div className="hud">
      <span className="hud-chip">Day {day}</span>
      {right}
    </div>
  );
}

/** Your words, as you wrote them, trimmed only for length. */
function yours(text: string, max = 120): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/** How long the darkroom plays before Keep playing starts to throb and the wait is named. */
const NUDGE_MS = 5000;

export function Darkroom({ day, words, lifting, onLeave, says, wait, label, note }: { day: number; words: string; lifting?: boolean; /** without it there is no way out: the darkroom straight after the idea roll, before anything is kept */ onLeave?: () => void; /** what the Moon says while it develops, in place of "Developing…" */ says?: string; /** the wait, named once the nudge comes */ wait?: string; /** the scene, for a screen reader, in place of "Your crew is developing your idea" */ label?: string; /** a line under the tray from the start, where Keep playing would be */ note?: string }) {
  // a few seconds of watching first; then say how long it takes and that leaving is fine
  const [nudge, setNudge] = useState(false);
  useEffect(() => {
    if (lifting) return;
    const t = setTimeout(() => setNudge(true), NUDGE_MS);
    return () => clearTimeout(t);
  }, [lifting]);
  return (
    <Stage bg="/art/reveal/darkroom.jpg" className={`darkroom ${lifting ? 'lifting' : ''}`} label={label ?? 'Your crew is developing your idea'}>
      <div className="safelight" />
      <Hud day={day} />
      <div className="tray-wrap">
        <img className="tray-moon" src="/art/reveal/moon-top.webp" alt="" draggable={false} />
        <div className="tray">
          <div className="liquid">
            <span className="ripple" />
            <span className="ripple r2" />
            <div className="print developing">
              <div className="print-pic">
                <img className="wash" src="/art/reveal/developing.jpg" alt="" draggable={false} />
                <img className="wash w2" src="/art/reveal/developing.jpg" alt="" draggable={false} />
                <span className="sweep" />
              </div>
              <p className="print-words">“{yours(words)}”</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bubble moon-says">{lifting ? 'Dry.' : <>{says ?? 'Developing'}<span className="dots3"><i /><i /><i /></span></>}</div>
      {!lifting && !onLeave && note && (
        <div className="darkroom-leave">
          <p className="bubble wait-note">{note}</p>
        </div>
      )}
      {!lifting && onLeave && (
        <div className={`darkroom-leave ${nudge ? 'nudge' : ''}`}>
          {nudge && <p className="bubble wait-note" aria-live="polite">{wait ?? 'Takes about a minute… don’t worry, you can pick this up later.'}</p>}
          <button type="button" className="btn darkroom-go" onClick={onLeave}>Keep playing ›</button>
        </div>
      )}
    </Stage>
  );
}

export type PrintsLayout = 'deck' | 'line' | 'tally';

/**
 * How long the print hangs still before it asks again. People waited on this
 * screen for something to happen (owner, 2026-10-08): with the print swinging,
 * the glow breathing, the rays turning and a ring pulsing, it read as a scene
 * still playing out, not one waiting for them. So it arrives, settles, and goes
 * still under a button-shaped "Turn it over"; only if nobody taps does it move
 * again, once every few seconds, to ask.
 */
const PEG_NUDGE_MS = 4000;

/** The print(s) on the line. With more than one dry, `layout` says how the rest wait (K40 prototypes, 2026-09-27). */
export function PrintOnPeg({ day, words, count = 1, layout = 'deck', onTurn, onLater, line, gold }: { day: number; words: string; count?: number; layout?: PrintsLayout; onTurn: () => void; onLater?: () => void; /** in place of "It’s dry." */ line?: string; /** the recast print (well/product/32): a gold edge, and its label is a credit */ gold?: boolean }) {
  const label = yours(words, 40);
  const more = Math.max(0, count - 1);
  const [nudge, setNudge] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setNudge(true), PEG_NUDGE_MS);
    return () => clearTimeout(t);
  }, []);
  // the whole wall turns it over: a tap that misses the print is still that tap
  const turned = useRef(false);
  const turn = () => { if (turned.current) return; turned.current = true; onTurn(); };
  return (
    <Stage bg="/art/reveal/wall.jpg" dim className={`peg-scene arriving layout-${layout} ${more ? 'many' : ''} ${nudge ? 'nudge' : ''} ${gold ? 'gold' : ''}`} label="A print from your crew, face-down" onClick={turn}>
      <Hud day={day} />
      <h1 className="stage-line">{line ?? (more ? `${count} prints are dry.` : 'It’s dry.')}</h1>
      <div className="line" />
      <button type="button" className="hanger" onClick={(e) => { e.stopPropagation(); turn(); }} aria-label="Turn it over">
        <span className="rays" />
        <span className="glow" />
        <img className="peg" src="/art/reveal/peg.webp" alt="" draggable={false} />
        <PrintStack label={label} day={day} more={layout === 'deck' ? more : 0} />
        {more > 0 && layout !== 'line' && <span className="count-badge">{layout === 'tally' ? `×${count}` : count}</span>}
        <span className="turn-pill">Turn it over</span>
      </button>
      {layout === 'line' && more > 0 && (
        <div className="waiting" aria-hidden>
          {Array.from({ length: Math.min(more, 2) }, (_, i) => (
            <span key={i} className="small-hanger">
              <img className="peg" src="/art/reveal/peg.webp" alt="" draggable={false} />
              <span className="print-back small" />
            </span>
          ))}
          {more > 2 && <span className="more-chip">+{more - 2}</span>}
        </div>
      )}
      <img className="peg-moon" src="/art/reveal/moon-shush.webp" alt="" draggable={false} />
      {onLater && <button type="button" className="hud-go" onClick={(e) => { e.stopPropagation(); onLater(); }}>Later</button>}
    </Stage>
  );
}

/** The face-down print, with the next ones' edges showing behind it when they wait as a deck. */
function PrintStack({ label, day, more }: { label: string; day: number; more: number }) {
  return (
    <span className="print-stack">
      {Array.from({ length: Math.min(more, 3) }, (_, i) => <span key={i} className={`print-back ghost g${i + 1}`} />)}
      <span className="print-back">
        <span className="back-words">{label}</span>
        <span className="back-day">Day {day}</span>
      </span>
    </span>
  );
}

/**
 * Kraben posts it (owner, 2026-09-28, K41 G1): a short scene after your tap, slow enough to watch.
 * Each beat adds a class (p-frame, p-snap, …); CSS does the motion, and the flight into the tab is
 * measured, so it lands on the Feed tab wherever the tab bar sits. A tap during it hurries it along;
 * it never skips it, so you always see where your picture went (owner, 2026-09-29).
 */
const POST_BEATS = [
  ['frame', 0], // Kraben rises into view, phone up; a viewfinder closes on the picture
  ['snap', 550], // the shutter
  ['card', 750], // the picture becomes a post: a white frame, your name, the day; the road comes up behind
  ['posted', 1050], // "Posted it for you."
  ['zip', 1350], // the post flies down into the Feed tab
  ['landed', 1750], // the tab takes it: a bump, the thumbnail on the tab
] as const;
type PostBeat = (typeof POST_BEATS)[number][0];
/** From your tap to the next screen: 3.5 s until 2026-10-30, about 2 s since the lean Day 1. */
export const POST_MS = 2100;
/** How much faster the rest of the post plays once you tap during it. */
const HURRY = 3;
/** A tap this soon after the one that started the post is that tap twice (a double tap), not a hurry. */
const DOUBLE_TAP_MS = 400;

export function PrintReveal({ day, words, url, credit, onDone, onPost }: { day: number; words: string; url: string; credit?: string; onDone: () => void; /** set, a tap has Kraben post it to your feed before going on (owner, 2026-09-28, K41 G1) */ onPost?: () => void }) {
  const [stage, setStage] = useState<'turning' | 'open' | 'posting'>('turning');
  const [beat, setBeat] = useState<PostBeat | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const post = useRef<HTMLDivElement>(null);
  const feedTab = useRef<HTMLSpanElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const left = useRef(false);
  // the post's own clock: `virt` ms of the sequence had played at wall time `at`, and it runs at `rate`
  const clock = useRef({ at: 0, virt: 0, rate: 1 });
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const done = useRef(-1); // the last beat shown, so a hurry reschedules only the ones still to come
  const [hurried, setHurried] = useState(false);

  useEffect(() => {
    // the picture is fetched while the print turns, so the full view never opens on a blank
    const img = new Image();
    img.src = url;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const t = setTimeout(() => setStage('open'), still ? 0 : 750);
    try { navigator.vibrate?.(12); } catch { /* not a phone */ }
    return () => clearTimeout(t);
  }, [url]);

  const go = () => { if (left.current) return; left.current = true; onDone(); };
  const schedule = () => {
    timers.current.forEach(clearTimeout);
    const { virt, rate } = clock.current;
    const wait = (at: number) => Math.max(0, (at - virt) / rate);
    timers.current = POST_BEATS.flatMap(([b, at], i) => (i > done.current ? [setTimeout(() => { done.current = i; setBeat(b); }, wait(at))] : []));
    timers.current.push(setTimeout(go, wait(POST_MS)));
  };
  useEffect(() => {
    if (stage !== 'posting') return;
    clock.current = { at: Date.now(), virt: 0, rate: 1 };
    schedule();
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  const hurry = () => {
    const c = clock.current;
    const now = Date.now();
    if (c.rate !== 1 || now - c.at < DOUBLE_TAP_MS) return;
    clock.current = { at: now, virt: c.virt + (now - c.at) * c.rate, rate: HURRY };
    schedule();
    setHurried(true);
  };
  useEffect(() => {
    if (beat === 'snap') { try { navigator.vibrate?.(10); } catch { /* not a phone */ } }
    if (beat !== 'zip' || !post.current || !feedTab.current || !post.current.animate) return;
    // measured, not guessed: from the post's middle to the Feed tab's icon
    const a = post.current.getBoundingClientRect();
    const b = feedTab.current.getBoundingClientRect();
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + 14 - (a.top + a.height / 2);
    const s = 34 / a.width;
    post.current.animate(
      [{ transform: 'none' }, { transform: `translate(${dx * 0.25}px, ${dy * 0.18 - 40}px) scale(0.55) rotate(-6deg)`, offset: 0.35 }, { transform: `translate(${dx}px, ${dy}px) scale(${s}) rotate(0deg)` }],
      { duration: 620, easing: 'cubic-bezier(.5,0,.3,1)', fill: 'forwards' },
    );
  }, [beat]);
  // hurried, every motion on screen plays at the same rate as the beats: what is running now, and each beat's as it starts
  useEffect(() => {
    if (hurried) root.current?.getAnimations?.({ subtree: true }).forEach((a) => { a.playbackRate = HURRY; });
  }, [hurried, beat]);
  // a tap on the open keeps it: with a feed to post to, Kraben snaps it first; a tap while he does hurries him
  const tap = () => {
    if (stage === 'posting') { hurry(); return; }
    if (!onPost) { go(); return; }
    onPost();
    try { navigator.vibrate?.(8); } catch { /* not a phone */ }
    setStage('posting');
  };

  if (stage === 'turning') {
    return (
      <Stage bg="/art/reveal/wall.jpg" dim className="peg-scene" label="Turning it over">
        <div className="line" />
        <div className="hanger turning">
          <img className="peg" src="/art/reveal/peg.webp" alt="" draggable={false} />
          <div className="flip" ref={card}>
            <span className="print-back face" />
            <span className="print-front face"><img src={url} alt="" /></span>
          </div>
        </div>
      </Stage>
    );
  }

  const posting = stage === 'posting';
  const reached = (b: PostBeat) => !!beat && POST_BEATS.findIndex(([x]) => x === beat) >= POST_BEATS.findIndex(([x]) => x === b);
  const beats = POST_BEATS.filter(([b]) => reached(b)).map(([b]) => `p-${b}`).join(' ');
  return (
    <div ref={root} className={`stage reveal-open ${posting ? `posting ${beats}` : ''}`} role="button" tabIndex={0} aria-label={onPost ? 'Your picture. Tap to keep it' : 'Your picture. Tap to keep going'} onClick={tap} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') tap(); }}>
      {posting && <img className="post-backdrop" src="/art/road/road-bg.jpg" alt="" draggable={false} />}
      <img className="reveal-img" src={url} alt={words} />
      <div className="reveal-foot">
        <p className="reveal-words">“{yours(words, 160)}”</p>
        <p className="reveal-credit">{credit ?? `Idea: you · Drawn by the Moon · Day ${day}`}</p>
        <p className="reveal-next">{onPost ? 'Tap to keep it' : 'Tap to keep going'}</p>
      </div>
      {/* the picture alone first; Kraben only comes in once you have had a proper look */}
      {!posting && (
        <div className="kraben-peek">
          <img src="/art/reveal/kraben-peek.webp" alt="" draggable={false} />
        </div>
      )}
      {posting && (
        <>
          <div className="viewfinder" aria-hidden><i /><i /><i /><i /></div>
          <div className="post-card" ref={post} aria-hidden>
            <img src={url} alt="" />
            <span className="post-card-foot"><b>You</b> · Day {day}<span className="post-card-words">{yours(words, 48)}</span></span>
          </div>
          <nav className="tabs post-tabs" aria-hidden>
            <span className="tab"><span className="tab-ico">⌂</span>Today</span>
            <span className="tab on"><span className="tab-ico">⋯</span>Road</span>
            <span className="tab feed-target" ref={feedTab}><span className="tab-ico">≡</span>Feed{reached('landed') && <span className="tab-new"><img src={url} alt="" /></span>}</span>
            <span className="tab"><span className="tab-ico">☺</span>Friends</span>
          </nav>
          <span className="shutter" aria-hidden />
          <div className="kraben-post" aria-live="polite">
            {reached('posted') && <span className="bubble">Posted it for you.</span>}
            <span className="post-flash" />
            <img src="/art/reveal/kraben-phone.webp" alt="" draggable={false} />
          </div>
        </>
      )}
    </div>
  );
}

/** On Home (K38 R6): the face-down print, set apart and lit, above everything. The whole block is the button. */
export function PrintTeaser({ day, words, count, onTurn, gold }: { day: number; words: string; count: number; onTurn: () => void; gold?: boolean }) {
  return (
    <button type="button" className={`stage inline peg-scene teaser ${gold ? 'gold' : ''}`} onClick={onTurn} aria-label="A print from your crew is dry. Turn it over">
      <img className="stage-bg" src="/art/reveal/wall.jpg" alt="" draggable={false} />
      <span className="stage-dim" />
      <span className="line" />
      <span className="hanger">
        <span className="rays" />
        <span className="glow" />
        <img className="peg" src="/art/reveal/peg.webp" alt="" draggable={false} />
        <PrintStack label={yours(words, 32)} day={day} more={count - 1} />
        {count > 1 && <span className="count-badge">{count}</span>}
      </span>
      <span className="teaser-line">{count > 1 ? `${count} prints are dry.` : 'A print is dry.'}<b>Turn it over</b></span>
    </button>
  );
}
